import React, { useEffect, useMemo, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  TextField,
  Divider,
  Button,
  CircularProgress,
  Stack,
} from '@mui/material';
import Mermaid from '../Mermaid';
import { generateContent } from '../../services/AIService';
import {
  assemblePatientLibrarySection7111,
  buildLiverModificationsMergePrompt,
  inferLiverPhaseBucket,
  DEFAULT_LIVER_PATIENT_LIBRARY_DECISIONS,
  STUDY_PHASE_BUCKET,
  LIVER_ALGORITHM,
  USE_STANDARD_ALGORITHM,
  RESTART_RECHALLENGE,
  MONITORING_PATHWAY,
} from '../../utils/patientLibrarySection7111';

const DECISIONS_PATH = 'template_language_fields.liver_patient_library_decisions';

const PatientLibrarySection7111Panel = ({
  values,
  setFieldValue,
  isFormDisabled,
}) => {
  const decisions =
    values?.template_language_fields?.liver_patient_library_decisions ||
    DEFAULT_LIVER_PATIENT_LIBRARY_DECISIONS;
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeError, setMergeError] = useState(null);

  const setDecision = useCallback(
    (field, value) => {
      setFieldValue(`${DECISIONS_PATH}.${field}`, value);
      // Clear prior AI merge when inputs that affect base narrative change
      if (
        field === 'protocol_modifications' ||
        field === 'use_standard_algorithm' ||
        field === 'study_phase_bucket' ||
        field === 'liver_algorithm' ||
        field === 'monitoring_pathway' ||
        field === 'monitoring_description'
      ) {
        setFieldValue(`${DECISIONS_PATH}.modified_narrative_override`, '');
      }
    },
    [setFieldValue]
  );

  // Default phase bucket from study overview when empty
  useEffect(() => {
    if (decisions.study_phase_bucket) return;
    const inferred = inferLiverPhaseBucket(values?.study_overview?.study_phase);
    if (inferred) {
      setFieldValue(`${DECISIONS_PATH}.study_phase_bucket`, inferred);
    }
  }, [
    decisions.study_phase_bucket,
    values?.study_overview?.study_phase,
    setFieldValue,
  ]);

  const assemblyInput = useMemo(
    () => ({
      template_language_fields: {
        liver_patient_library_decisions: decisions,
      },
    }),
    [
      decisions.study_phase_bucket,
      decisions.liver_algorithm,
      decisions.use_standard_algorithm,
      decisions.protocol_modifications,
      decisions.restart_rechallenge,
      decisions.restart_requirements,
      decisions.monitoring_pathway,
      decisions.monitoring_description,
      decisions.modified_narrative_override,
    ]
  );

  const assembly = useMemo(
    () => assemblePatientLibrarySection7111(assemblyInput),
    [assemblyInput]
  );

  const syncTemplateFields = useCallback(() => {
    // Never wipe previously saved liver content when decisions are incomplete/empty
    // (e.g. after lock reinitialize before nested decisions hydrate).
    if (!assembly.liver_event_stopping_criteria && !assembly.liver_event_stopping_flowchart_mermaid) {
      return;
    }
    if (assembly.liver_event_stopping_criteria) {
      setFieldValue(
        'template_language_fields.liver_event_stopping_criteria',
        assembly.liver_event_stopping_criteria
      );
    }
    if (assembly.liver_event_stopping_flowchart_mermaid) {
      setFieldValue(
        'template_language_fields.liver_event_stopping_flowchart_mermaid',
        assembly.liver_event_stopping_flowchart_mermaid
      );
    }
    if (assembly.liver_restart_rechallenge) {
      setFieldValue(
        'template_language_fields.liver_restart_rechallenge',
        assembly.liver_restart_rechallenge
      );
    }
  }, [assembly, setFieldValue]);

  useEffect(() => {
    syncTemplateFields();
  }, [syncTemplateFields]);

  const handleApplyModifications = async () => {
    const mods = String(decisions.protocol_modifications || '').trim();
    if (!mods || !assembly.liver_event_stopping_criteria) return;

    setMergeBusy(true);
    setMergeError(null);
    try {
      // Rebuild base without override for merge input
      const baseAssembly = assemblePatientLibrarySection7111({
        template_language_fields: {
          liver_patient_library_decisions: {
            ...decisions,
            modified_narrative_override: '',
          },
        },
      });
      const prompt = buildLiverModificationsMergePrompt({
        baseNarrative: baseAssembly.liver_event_stopping_criteria,
        modifications: mods,
        decisions,
      });
      const text = await generateContent(prompt, '', '', 'liver_7111_modifications', '7.1.1.1');
      const merged = String(text || '').trim();
      if (!merged) {
        throw new Error('Empty response from AI merge.');
      }
      setFieldValue(`${DECISIONS_PATH}.modified_narrative_override`, merged);
      setFieldValue('template_language_fields.liver_event_stopping_criteria', merged);
    } catch (e) {
      setMergeError(e.message || 'Could not apply modifications.');
    } finally {
      setMergeBusy(false);
    }
  };

  return (
    <Box sx={{ mb: 3, p: 2, border: '1px solid #dbe3eb', borderRadius: 1, bgcolor: '#fafbfc' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        TransCelerate Patient Library — Section 7.1.1.1 Liver Event Stopping Criteria
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select the liver algorithm decision tree. Narrative and flowchart are assembled from the
        Patient Library (verbatim when standard algorithm is selected). Protocol modifications and
        custom monitoring use a narrow AI merge only when selected below. Flowchart is patched from
        restart and monitoring choices.
      </Typography>

      {assembly.warnings.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {assembly.warnings.map((w) => (
            <div key={w}>{w}</div>
          ))}
        </Alert>
      )}
      {mergeError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMergeError(null)}>
          {mergeError}
        </Alert>
      )}

      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isFormDisabled}>
        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
          Study phase (algorithm family)
        </FormLabel>
        <RadioGroup
          value={decisions.study_phase_bucket || ''}
          onChange={(e) => setDecision('study_phase_bucket', e.target.value)}
        >
          <FormControlLabel
            value={STUDY_PHASE_BUCKET.PHASE_1}
            control={<Radio size="small" />}
            label="Phase 1"
          />
          <FormControlLabel
            value={STUDY_PHASE_BUCKET.PHASE_2_3}
            control={<Radio size="small" />}
            label="Phase 2 / Phase 3"
          />
        </RadioGroup>
      </FormControl>

      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isFormDisabled}>
        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
          1. Which liver chemistry algorithm should be used?
        </FormLabel>
        <RadioGroup
          value={decisions.liver_algorithm || ''}
          onChange={(e) => setDecision('liver_algorithm', e.target.value)}
        >
          <FormControlLabel
            value={LIVER_ALGORITHM.ALT_ONLY}
            control={<Radio size="small" />}
            label="ALT Only"
          />
          <FormControlLabel
            value={LIVER_ALGORITHM.ALT_OR_AST}
            control={<Radio size="small" />}
            label="ALT or AST"
          />
        </RadioGroup>
      </FormControl>

      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isFormDisabled}>
        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
          2. Should the standard TransCelerate liver event stopping algorithm be used?
        </FormLabel>
        <RadioGroup
          value={decisions.use_standard_algorithm || ''}
          onChange={(e) => {
            const next = e.target.value;
            setDecision('use_standard_algorithm', next);
            if (next === USE_STANDARD_ALGORITHM.YES) {
              setDecision('protocol_modifications', '');
            }
          }}
        >
          <FormControlLabel
            value={USE_STANDARD_ALGORITHM.YES}
            control={<Radio size="small" />}
            label="Yes – Use the standard TransCelerate algorithm"
          />
          <FormControlLabel
            value={USE_STANDARD_ALGORITHM.NO}
            control={<Radio size="small" />}
            label="No – The protocol specifies modifications"
          />
        </RadioGroup>
      </FormControl>

      {decisions.use_standard_algorithm === USE_STANDARD_ALGORITHM.NO && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Protocol modifications"
            value={decisions.protocol_modifications || ''}
            onChange={(e) => setDecision('protocol_modifications', e.target.value)}
            disabled={isFormDisabled}
            helperText="Describe only the changes from the standard algorithm. Stopping logic is otherwise preserved."
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
            <Button
              size="small"
              variant="contained"
              onClick={handleApplyModifications}
              disabled={
                isFormDisabled ||
                mergeBusy ||
                !String(decisions.protocol_modifications || '').trim()
              }
            >
              {mergeBusy ? <CircularProgress size={18} /> : 'Apply Modifications'}
            </Button>
            {decisions.modified_narrative_override ? (
              <Typography variant="caption" color="success.main">
                Modified narrative applied
              </Typography>
            ) : null}
          </Stack>
        </Box>
      )}

      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isFormDisabled}>
        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
          3. After liver event stopping criteria are met, what is the protocol requirement?
        </FormLabel>
        <RadioGroup
          value={decisions.restart_rechallenge || ''}
          onChange={(e) => {
            const next = e.target.value;
            setDecision('restart_rechallenge', next);
            setFieldValue(
              'discontinuation_rules.rechallenge_decisions.liver_restart_after_stopping',
              next
            );
            if (next === RESTART_RECHALLENGE.NOT_PERMITTED) {
              setDecision('restart_requirements', '');
            }
          }}
        >
          <FormControlLabel
            value={RESTART_RECHALLENGE.NOT_PERMITTED}
            control={<Radio size="small" />}
            label="Restart/Rechallenge NOT permitted"
          />
          <FormControlLabel
            value={RESTART_RECHALLENGE.PERMITTED}
            control={<Radio size="small" />}
            label="Restart/Rechallenge permitted"
          />
        </RadioGroup>
      </FormControl>

      {decisions.restart_rechallenge === RESTART_RECHALLENGE.PERMITTED && (
        <TextField
          fullWidth
          multiline
          minRows={3}
          sx={{ mb: 2 }}
          label="Protocol-specific restart/rechallenge requirements"
          value={decisions.restart_requirements || ''}
          onChange={(e) => setDecision('restart_requirements', e.target.value)}
          disabled={isFormDisabled}
        />
      )}

      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isFormDisabled}>
        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
          4. For participants who do not meet liver stopping criteria but require continued
          monitoring, which pathway should be used?
        </FormLabel>
        <RadioGroup
          value={decisions.monitoring_pathway || ''}
          onChange={(e) => {
            const next = e.target.value;
            setDecision('monitoring_pathway', next);
            if (next === MONITORING_PATHWAY.STANDARD) {
              setDecision('monitoring_description', '');
            }
          }}
        >
          <FormControlLabel
            value={MONITORING_PATHWAY.STANDARD}
            control={<Radio size="small" />}
            label="Use standard TransCelerate increased monitoring pathway"
          />
          <FormControlLabel
            value={MONITORING_PATHWAY.PROTOCOL_SPECIFIC}
            control={<Radio size="small" />}
            label="Use protocol-specific monitoring pathway"
          />
        </RadioGroup>
      </FormControl>

      {decisions.monitoring_pathway === MONITORING_PATHWAY.PROTOCOL_SPECIFIC && (
        <TextField
          fullWidth
          multiline
          minRows={3}
          sx={{ mb: 2 }}
          label="Describe the monitoring pathway"
          value={decisions.monitoring_description || ''}
          onChange={(e) => setDecision('monitoring_description', e.target.value)}
          disabled={isFormDisabled}
        />
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Assembled Section 7.1.1.1 preview
      </Typography>
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          bgcolor: '#fff',
          border: '1px solid #e5eaf0',
          borderRadius: 1,
          whiteSpace: 'pre-wrap',
          fontSize: 13,
          maxHeight: 280,
          overflow: 'auto',
        }}
      >
        {assembly.liver_event_stopping_criteria || (
          <Typography variant="body2" color="text.secondary">
            Complete the questions above to assemble liver stopping criteria.
          </Typography>
        )}
      </Box>

      {assembly.liver_restart_rechallenge ? (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Assembled Section 7.1.3.1 preview
          </Typography>
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              bgcolor: '#fff',
              border: '1px solid #e5eaf0',
              borderRadius: 1,
              whiteSpace: 'pre-wrap',
              fontSize: 13,
              maxHeight: 200,
              overflow: 'auto',
            }}
          >
            {assembly.liver_restart_rechallenge}
          </Box>
        </>
      ) : null}

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Liver event stopping flowchart (patched)
      </Typography>
      {assembly.liver_event_stopping_flowchart_mermaid ? (
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid #e5eaf0',
            borderRadius: 1,
            overflow: 'auto',
          }}
        >
          <Mermaid chart={assembly.liver_event_stopping_flowchart_mermaid} />
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Flowchart preview appears after phase and algorithm are selected.
        </Typography>
      )}
    </Box>
  );
};

export default PatientLibrarySection7111Panel;
